import classnames from 'classnames';
import styles from './styles';
import { withStyles } from '@material-ui/core/styles';
import { format, isToday, parse } from 'date-fns';
import { useMemo, useState, useEffect, useRef } from 'react';
import Popper from '@material-ui/core/Popper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MonthPreviewModal from '../MonthPreviewModal/MonthPreviewModal';
import Typography from '@material-ui/core/Typography';
import ArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';import { useDateSettingsFormat } from 'common/useDateSettingsFormat';
import { getSheetEventTitle } from 'pages/ScheduleV6/utils';

const DayCell = ({
  classes,
  data,
  clickedDay,
  setClickedDay,
  sheetsWithoutForemanIds,
  showAllLocations,
  locations,
}) => {
  const { dateFormat } = useDateSettingsFormat();
  const getDayNumber = (date) => format(parse(date, dateFormat, new Date()), 'dd');
  const modalRef = useRef(null);
  const popperRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openedWLId, setOpenedWLId] = useState(null);

  const isClickedDay = useMemo(() => clickedDay === data.dayNumber, [clickedDay]);
  const previewItems = useMemo(
    () =>
      data.events
        ?.slice()
        .sort((a, b) => new Date(b.createdAtDate) - new Date(a.createdAtDate))
        .slice(0, 3) || [],
    [clickedDay, data.events]
  );

  const openWLAccordion = (id) => {
    setOpenedWLId(id);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (isClickedDay) setAnchorEl(modalRef.current);
  }, []);

  const handleClose = (e) => {
    const clickedElement = e.target;
    const rootElement = document.getElementById('root');

    if (
      //? to close MonthPreviewModal for the day ->
      rootElement?.contains(clickedElement) && // clickedElement is not an activity in a drawer
      clickedElement.textContent != '+ Add worklog' && //and clickedElement is not an '+ Add worklog' btn
      !(clickedElement.parentNode.id === 'manageButtons' || clickedElement.id === 'manageButtons')
    ) {
      e?.stopPropagation();
      setAnchorEl(null);
    }
  };

  const getStatusColor = (sheet) => {
    if (sheet.canceledAt) return '#D14646';
    if (sheet.verificationStatus === 'verified') return '#0C842B';
    if (sheet.verificationStatus === 'pending') return '#3E82FF';
    if (sheet.submitted || sheet.submittedAt) return '#3E82FF';
    if (sheet.published) return '#979797';
    return '#D14646';
  }
  const getStatusLetter = (sheet) => {
    if (sheet.canceledAt) return 'C';
    if (sheet.verificationStatus === 'verified') return 'V';
    if (sheet.verificationStatus === 'pending') return 'PV'
    if (sheet.submitted || sheet.submittedAt) return 'S';
    if (sheet.published) return 'P';
    return 'D';
  }

  const getLocationColor = (sheet) => {
    return locations.find(({ _id }) => sheet.tenantLocationId === _id)?.color;
  }

  const open = Boolean(anchorEl);

  const handleUpdate = () => {
    if (popperRef.current && popperRef.current.update) {
      setTimeout(() => {
        popperRef.current.update();
      }, 0);
    }
  };
  const id = 'simple-popover' + data.dayNumber;
  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className={classnames(classes.dayCellInner)}>
        <div
          ref={modalRef}
          onClick={(e) => {
            handleClick(e);
            setClickedDay(data.dayNumber);
          }}
          className={classnames(
            classes.square,
            clickedDay === data.dayNumber && classes.clickedDay
          )}
        >
          <div className={classnames(classes.dayCellHeder)}>
            <span
              className={classnames(
                classes.dayNumber,
                isToday(parse(data.dayNumber, dateFormat, new Date())) && classes.todayNumber
              )}
            >
              {getDayNumber(data.dayNumber)}
            </span>

            <div
              className={classnames(
                classes.sheetQuantity,
                isClickedDay && classes.clickedDayQuantity
              )}
            >
              {data.events?.length}
            </div>
          </div>
          <div className={classnames(classes.dayCellBody)}>
            {previewItems.map((sheet) => (
              <div
                key={sheet?._id}
                className={classnames(
                  classes.previewCellItemWrapper,
                  sheetsWithoutForemanIds.includes(sheet._id) && classes.missingCL
                )}
              >
                <div className={classnames(classes.previewItemInner)}>
                  <div
                    className={classnames(
                      classes.previewCellImgWrapper,
                      clickedDay === data.dayNumber
                        ? classes.activeIconWrapper
                        : classes.notActiveIconWrapper,
                      showAllLocations ? classes.locationMark : ''
                    )}
                    style={{
                      color: getStatusColor(sheet),
                      borderColor: showAllLocations ? getLocationColor(sheet) : getStatusColor(sheet),
                    }}
                  >
                    {showAllLocations ? ' ' : getStatusLetter(sheet)}
                  </div>
                  <Typography
                    variant="body1"
                    className={classnames(
                      classes.previewCellItem,
                      Boolean(sheet.canceledAt) && classes.canceled
                    )}
                    color="textPrimary"
                  >
                    {getSheetEventTitle(sheet)}
                  </Typography>
                </div>
                <ArrowRightIcon
                  color="secondary"
                  fontSize={'small'}
                  className={classnames(classes.previewCellArrow)}
                  onClick={() => {}}
                />
              </div>
            ))}
          </div>
        </div>
        <Popper
          id={id}
          ref={popperRef}
          open={open}
          disablePortal
          anchorEl={anchorEl || modalRef}
          placement={'right-start'}
          className={classnames(classes.popper)}
          flip={false}
          popperRef={(node) => {
            popperRef.current = node;
          }}
          modifiers={{
            flip: {
              enabled: false,
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: 'viewport',
            },
          }}
        >
          <MonthPreviewModal
            data={data}
            onClose={handleClose}
            openedWLId={openedWLId}
            openWLAccordion={openWLAccordion}
            sheetsWithoutForemanIds={sheetsWithoutForemanIds}
            handleUpdate={handleUpdate}
            showAllLocations={showAllLocations}
            locations={locations}
          />
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default withStyles(styles)(DayCell);
